// contact.service.ts
import { Injectable, inject, signal } from '@angular/core';
import {
  DeviceInfo,
  DeviceInfoService,
  LoadingService,
  LanguageService,
} from '@awo-care/shared-core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Functions, httpsCallable } from 'firebase/functions';
import {
  collection,
  getDocs,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import {
  FIREBASE_FIRESTORE,
  FIREBASE_FUNCTIONS,
} from '@awo-care/shared-firebase';

/** Ein Kind des eingeloggten Elternteils — nur, was die Auswahl im Formular braucht. */
export interface ContactChild {
  id: string;
  firstName: string;
  lastName: string;
  schoolId: string;
}

export interface ContactForm {
  email: string;
  name: string;
  category: string;
  message: string;
  deviceInfo: DeviceInfo;
  recaptchaToken?: string;
  facilityId?: string;
  facilityName?: string;
  referenceNumber?: string;
}

interface CreateTicketResponse {
  success: boolean;
  ticketId?: string;
  error?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ContactService {
  private functions = inject(FIREBASE_FUNCTIONS);
  private firestore = inject(FIREBASE_FIRESTORE);
  private loadingService = inject(LoadingService);
  private snackBar = inject(MatSnackBar);
  private deviceInfoService = inject(DeviceInfoService);
  private languageService = inject(LanguageService);

  private readonly categoryMap: Record<string, string> = {
    Feedback: 'general',
    Problem: 'technical',
    'Rückfrage': 'general',
    Zahlung: 'billing',
    Sonstiges: 'other',
  };

  showSuccessMessage = signal(false);

  /**
   * Aktive Kinder des eingeloggten Elternteils (#1145). Gleiche Abfrage wie
   * im Kundenportal unter „Meine Kinder“ — Index und Security Rule gibt es
   * dafür schon.
   */
  async loadOwnChildren(uid: string): Promise<ContactChild[]> {
    const snapshot = await getDocs(
      query(
        collection(this.firestore, 'students'),
        where('userId', '==', uid),
        where('isActive', '==', true),
        orderBy('createdOn', 'desc')
      )
    );
    return snapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        firstName: typeof data['firstName'] === 'string' ? data['firstName'] : '',
        lastName: typeof data['lastName'] === 'string' ? data['lastName'] : '',
        schoolId: typeof data['schoolId'] === 'string' ? data['schoolId'] : '',
      };
    });
  }

  async submitContactForm(
    formData: ContactForm,
    recaptchaToken: string
  ): Promise<boolean> {
    this.loadingService.setLoading(true);

    try {
      const deviceInfo = this.deviceInfoService.getDeviceInfo() as DeviceInfo;
      deviceInfo.version = deviceInfo.version || 'unknown';
      deviceInfo.language = this.languageService.currentLanguage().code;

      const createTicket = httpsCallable<unknown, CreateTicketResponse>(
        this.functions,
        'createTicketFromContact'
      );

      const result = await createTicket({
        title: formData.message.substring(0, 100),
        description: formData.message,
        category: this.categoryMap[formData.category] || 'general',
        email: formData.email,
        name: formData.name,
        recaptchaToken,
        facilityId: formData.facilityId || '',
        facilityName: formData.facilityName || '',
        referenceNumber: formData.referenceNumber?.trim() || '',
        deviceInfo: {
          os: deviceInfo.os,
          browser: deviceInfo.browser,
          resolution: deviceInfo.screenResolution,
          language: deviceInfo.language,
        },
      });

      if (result.data.success) {
        this.showSuccessMessage.set(true);
        return true;
      } else {
        throw new Error(result.data.error || 'Unknown error');
      }
    } catch (error: unknown) {
      console.error('Error submitting contact form:', error);
      this.showErrorMessage(
        error instanceof Error ? error.message : 'Unknown error'
      );
      return false;
    } finally {
      this.loadingService.setLoading(false);
    }
  }

  private showErrorMessage(message: string): void {
    this.snackBar.open(
      `Fehler beim Senden der Nachricht: ${message}. Bitte versuchen Sie es erneut.`,
      $localize`:@@common.close:Schließen`,
      {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
      }
    );
  }
}
